import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import Labels from "../labels/Labels";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import AddAdministrativeFee from "../components/AddAdministrativeFee";
import { AdministrativeFeesService } from "../services/AdministrativeFeesService";
import RenderOnRole from "../components/RenderOnRole";

// Helper function to extract error message from backend response
function getErrorMessage(error, defaultMessage) {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data) {
    return typeof error.response.data === "string"
      ? error.response.data
      : defaultMessage;
  }
  if (error?.message) {
    return error.message;
  }
  return defaultMessage;
}

export default function AdministrativeFeesConfig() {
  const [feedback, setFeedback] = React.useState({
    open: false,
    type: "error",
    message: "",
  });
  const [addFeeDlg, setAddFeeDlg] = React.useState(false);
  const [selectedFeeType, setSelectedFeeType] = useState("");
  const queryClient = useQueryClient();

  // Fetch employers to map company ID to employer name
  const { data: employersData } = useQuery(
    ["employers"],
    () =>
      HttpService.getAxiosClient().get(
        window.API_URL + `/employer/all?excludeRelations=true`
      ),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const employerMap = useMemo(() => {
    const map = {};
    if (employersData?.data) {
      employersData.data.forEach((employer) => {
        map[employer.idEmployer] = employer.name;
      });
    }
    return map;
  }, [employersData]);

  // Fetch fee types for filter dropdown
  const { data: feeTypesData } = useQuery(
    ["feeTypes"],
    async () =>
      HttpService.getAxiosClient().get(window.API_URL + `/fee-types/all`),
    {
      refetchOnWindowFocus: false,
    }
  );

  const feeTypeOptions = useMemo(() => {
    return feeTypesData?.data || [];
  }, [feeTypesData]);

  const deleteAdministrativeFeeMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL + `/administrative-fees/${values.id}`
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["administrativeFees"]);
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "administrativeFees.DeleteSuccess",
            "Administrative fee deleted successfully"
          ),
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: getErrorMessage(
            error,
            Labels.getLabel(
              "administrativeFees.ErrorDeleting",
              "Error deleting the administrative fee"
            )
          ),
        });
      },
    }
  );

  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel("administrativeFees.column.id", "ID"),
      width: 100,
    },
    {
      field: "feeTypeDescription",
      headerName: Labels.getLabel(
        "administrativeFees.column.feeTypeDescription",
        "Fee Type Description"
      ),
      width: 250,
    },
    {
      field: "feeDate",
      headerName: Labels.getLabel(
        "administrativeFees.column.feeDate",
        "Fee Date"
      ),
      width: 150,
      valueFormatter: (params) => {
        if (params.value) {
          // Parse date string (YYYY-MM-DD) without timezone conversion
          const [year, month, day] = params.value.split("-");
          const localDate = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day)
          );
          return localDate.toLocaleDateString();
        }
        return "";
      },
    },
    {
      field: "amount",
      headerName: Labels.getLabel("administrativeFees.column.amount", "Amount"),
      width: 150,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(params.value);
        }
        return "";
      },
    },
    {
      field: "companyId",
      headerName: Labels.getLabel(
        "administrativeFees.column.companyName",
        "Company"
      ),
      width: 200,
      valueGetter: (params) => {
        const companyId = params.row?.companyId;
        if (!companyId) return "";
        return employerMap[companyId] || companyId;
      },
    },
    {
      field: "action",
      headerName: Labels.getLabel("administrativeFees.column.action", "Action"),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: "flex" }}>
          <RenderOnRole roles={["pc-program-super-admin"]}>
            <IconButton
              onClick={() =>
                deleteAdministrativeFeeMutation.mutate({ ...params })
              }
              title={Labels.getLabel("administrativeFees.Delete", "Delete")}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </RenderOnRole>
        </div>
      ),
    },
  ];

  const { data, isLoading, error, isFetching } = useQuery(
    ["administrativeFees"],
    async () => {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/administrative-fees/all`
      );
      return response;
    },
    {
      keepPreviousData: true,
      staleTime: 3000,
      refetchOnWindowFocus: false,
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: getErrorMessage(
            error,
            Labels.getLabel(
              "administrativeFees.ErrorLoading",
              "Error loading administrative fees"
            )
          ),
        });
      },
    }
  );

  const sortedData = useMemo(() => {
    let filtered = data?.data
      ? [...data.data].sort((a, b) => {
          // Sort by feeDate in descending order (most recent first)
          const dateA = new Date(a.feeDate).getTime();
          const dateB = new Date(b.feeDate).getTime();
          return dateB - dateA;
        })
      : [];

    // Apply fee type filter if selected
    if (selectedFeeType) {
      filtered = filtered.filter((fee) => fee.feeTypeId === selectedFeeType);
    }

    return filtered;
  }, [data, selectedFeeType]);

  const addFeeDialog = (
    <Dialog open={addFeeDlg} fullWidth onClose={() => {}}>
      <Box
        sx={{
          backgroundColor: "primary.main",
          px: 3,
          minHeight: 52,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionIcon sx={{ color: "white" }} />
          <Typography variant="h6" sx={{ color: "white" }}>
            {Labels.getLabel(
              "administrativeFees.AddAdministrativeFee",
              "Add Administrative Fee"
            )}
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            setAddFeeDlg(false);
          }}
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ paddingTop: 3 }}>
        <AddAdministrativeFee handleClose={() => setAddFeeDlg(false)} />
      </DialogContent>
    </Dialog>
  );

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        <Typography>
          {Labels.getLabel(
            "administrativeFees.Loading",
            "Loading administrative fees..."
          )}
        </Typography>
      ) : error ? (
        <Alert severity="error">
          {getErrorMessage(
            error,
            Labels.getLabel(
              "administrativeFees.ErrorLoading",
              "Error loading administrative fees"
            )
          )}
        </Alert>
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2, gap: 2, flexWrap: "wrap" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h5">
                  {Labels.getLabel(
                    "administrativeFees.AdministrativeFees",
                    "Administrative Fees"
                  )}
                </Typography>
                <IconButton
                  onClick={() =>
                    queryClient.invalidateQueries(["administrativeFees"])
                  }
                  title={Labels.getLabel(
                    "administrativeFees.Refresh",
                    "Refresh"
                  )}
                  size="small"
                >
                  <RefreshIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="fee-type-filter-label">
                    {Labels.getLabel(
                      "administrativeFees.FilterByFeeType",
                      "Filter by Fee Type"
                    )}
                  </InputLabel>
                  <Select
                    labelId="fee-type-filter-label"
                    id="fee-type-filter"
                    value={selectedFeeType}
                    onChange={(e) => setSelectedFeeType(e.target.value)}
                    label={Labels.getLabel(
                      "administrativeFees.FilterByFeeType",
                      "Filter by Fee Type"
                    )}
                  >
                    <MenuItem value="">
                      {Labels.getLabel(
                        "administrativeFees.AllFeeTypes",
                        "All Fee Types"
                      )}
                    </MenuItem>
                    {feeTypeOptions.map((feeType) => (
                      <MenuItem key={feeType.id} value={feeType.id}>
                        {feeType.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <RenderOnRole roles={["pc-program-super-admin"]}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setAddFeeDlg(true)}
                  >
                    {Labels.getLabel(
                      "administrativeFees.AddAdministrativeFee",
                      "Add Administrative Fee"
                    )}
                  </Button>
                </RenderOnRole>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
              rows={sortedData}
              columns={columns}
              components={{
                Toolbar: GridToolbar,
              }}
              disableSelectionOnClick
              loading={isFetching}
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
              }}
            />
          </Grid>
        </Grid>
      )}

      {addFeeDialog}

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
